/* eslint-disable @typescript-eslint/member-ordering */
import { noop } from "./utils/utils";
let instanceCounter = 0;
class BackendRelayer {
    socket;
    requestIdCounter = 0;
    pendingRequests = {};
    // eslint-disable-next-line @typescript-eslint/ban-types
    functionRegistry = {};
    logResponses;
    address;
    logPrefix;
    closedByUser = false;
    id;
    reconnecting = false;
    constructor(address, onStatusChange, logResponses, logPrefix) {
        this.id = ++instanceCounter;
        this.logPrefix = `BackendRelayer #${this.id} (${logPrefix})`;
        this.logResponses = logResponses;
        this.address = address;
        this.socket = this.connect(address, onStatusChange);
    }
    connect(address, onStatusChange) {
        const baseAddress = address.substring(0, address.indexOf("?"));
        console.log(this.logPrefix, "connecting", baseAddress);
        const socket = new WebSocket(address);
        socket.addEventListener("message", (event) => {
            try {
                const response = JSON.parse(event.data);
                const { id, result, error, stackTrace, functionId, params } = response;
                if (this.logResponses) {
                    console.log(this.logPrefix, "RPC: New response", response);
                }
                if (functionId) {
                    if (!this.functionRegistry[functionId])
                        throw new Error(`Function ${functionId} not found in registry`);
                    // Handle function invocation
                    try {
                        this.functionRegistry[functionId](...params);
                    }
                    catch {
                        console.error(this.logPrefix, `Error invoking client function ${functionId}`);
                    }
                    return;
                }
                if (id === undefined) {
                    throw new Error(`Message: ${String(event.data)}`);
                }
                if (this.pendingRequests[id]) {
                    if (error) {
                        const err = new Error(error);
                        err.stack = stackTrace;
                        this.pendingRequests[id](Promise.reject(err));
                        // if (this.logResponses) console.warn(this.logPrefix, err)
                    }
                    else {
                        let deserializedResult = result;
                        if (result && result.__isFunction) {
                            deserializedResult = (...params) => {
                                if (this.socket.readyState === WebSocket.OPEN) {
                                    this.socket.send(JSON.stringify({
                                        functionId: result.functionId,
                                        params,
                                    }));
                                }
                            };
                        }
                        this.pendingRequests[id](Promise.resolve(deserializedResult));
                    }
                    delete this.pendingRequests[id];
                }
            }
            catch (error) {
                if (this.logResponses)
                    console.error(this.logPrefix, "failure:", error);
            }
        });
        socket.addEventListener("open", () => {
            this.reconnecting = false;
            console.log(this.logPrefix, "connected", baseAddress);
            onStatusChange("connected");
        });
        socket.addEventListener("close", (event) => {
            if (this.reconnecting)
                return;
            let reason = event.reason || "unknown";
            if (event.code === 1006 && reason === "unknown") {
                reason = "Server is unreachable.";
            }
            console.log(this.logPrefix, "closed:", event.code, reason, baseAddress);
            onStatusChange("closed", `${event.code}: ${reason}`);
            if (this.closedByUser || event.code === 1008)
                return;
            this.reconnecting = true;
            setTimeout(() => {
                this.socket = this.connect(address, onStatusChange);
            }, 1_000);
            //   backOff(
            //     () => {
            //       this.socket = this.connect(address, onStatusChange)
            //       if (this.socket.readyState !== WebSocket.OPEN) {
            //         throw new Error("Reconnect attempt failed")
            //       }
            //       return Promise.resolve()
            //     },
            //     {
            //       maxDelay: isDevelopment ? 1_000 : 10_000,
            //       numOfAttempts: 100,
            //       retry: (_err, attempt) => {
            //         console.log(this.logPrefix, `reconnecting, attempt: #${attempt}`)
            //         return !(this.closedByUser || event.code === 1008)
            //       },
            //       startingDelay: 100,
            //     }
            //   ).catch(() => {
            //     console.error(this.logPrefix, "Failed to reconnect")
            //     this.reconnecting = false
            //   })
        });
        socket.addEventListener("error", (event) => {
            if (this.reconnecting)
                return;
            console.error(this.logPrefix, "error:", event, baseAddress);
        });
        return socket;
    }
    async requestFromBackend(method, params) {
        await this.ensureConnection();
        //
        const id = this.requestIdCounter++;
        const serializedParams = params.map((param, paramIndex) => {
            if (typeof param === "function") {
                const functionId = `client_func_${id}_${paramIndex}`;
                this.functionRegistry[functionId] = param;
                return { __isFunction: true, functionId };
            }
            if (typeof param === "undefined") {
                return "undefined";
            }
            return param;
        });
        if (this.socket.readyState !== WebSocket.OPEN) {
            return Promise.reject(new Error("WebSocket is not open"));
        }
        return new Promise((resolve) => {
            this.pendingRequests[id] = resolve;
            this.socket.send(JSON.stringify({ id, method, params: serializedParams }));
        });
    }
    async ensureConnection() {
        if (this.socket.readyState === WebSocket.OPEN)
            return;
        if (this.closedByUser)
            throw new Error("Connection closed by user");
        return new Promise((resolve) => {
            this.socket.addEventListener("open", () => resolve());
        });
    }
    closeConnection() {
        try {
            this.closedByUser = true;
            if (this.socket &&
                this.socket.readyState !== WebSocket.CLOSING &&
                this.socket.readyState !== WebSocket.CLOSED) {
                this.socket.close(1000, "Client closed connection");
            }
            // Clean up pending requests to prevent memory leaks
            Object.values(this.pendingRequests).forEach((reject) => reject(new Error("Connection closed")));
            this.pendingRequests = {};
        }
        catch {
            // Ignore errors
        }
    }
}
export function createBackendRelayer(address, onStatusChange = noop, logResponses = true, logPrefix = "") {
    const relayer = new BackendRelayer(address, onStatusChange, logResponses, logPrefix);
    return new Proxy(relayer, {
        get: (target, property) => {
            if (typeof property === "string" && !(property in target)) {
                return async (...args) => {
                    return target.requestFromBackend(property, args);
                };
            }
            return target[property];
        },
    });
}
//# sourceMappingURL=backend-relayer.js.map